import { ComponentProps, useMemo, useRef } from 'react'
import clsx from 'clsx'
import { useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { CancellationForm } from '@/components/Cancellation/CancellationForm'
import {
  getEmploymentTypeNotification,
  isPaymentProtectionOffer,
  isUnsupportedEmploymentType,
} from '@/components/EmploymentType/employmentType.utils'
import { EmploymentTypeSelect } from '@/components/EmploymentType/EmploymentTypeSelect'
import { useOptimisticEmploymentType } from '@/components/EmploymentType/useOptimisticEmploymentType'
import { InfoCard } from '@/components/InfoCard/InfoCard'
import { ProductCard } from '@/components/ProductCard/ProductCard'
import { useHasScrolledPast } from '@/components/ProductPage/ScrollPast/useHasScrolledPast'
import { useOfferToEdit } from '@/components/ProductPage/useOfferToEdit'
import { CartDiscount } from '@/features/CartDiscount/CartDiscount'
import { useSelectedOfferValueOrThrow } from '@/features/priceCalculator/PurchaseForm/useSelectedOffer'
import { usePriceCalculatorStep } from '@/features/priceCalculator/usePriceCalculatorStep'
import { type ProductOfferFragment } from '@/services/graphql/generated'
import { useShopSessionValueOrThrow } from '@/services/shopSession/ShopSessionContext'
import { useTracking } from '@/services/Tracking/useTracking'
import { includesYoungPetDiscount } from '@/utils/discounts.utils'
import { useAddToCart } from '@/utils/useAddToCart'
import { Button } from 'ui/src/components/Button/Button'
import { Divider } from 'ui/src/components/Divider/Divider'
import { ArrowDownIcon } from 'ui/src/icons/ArrowIcon'
import { yStack } from 'ui/src/patterns/stack'
import { sprinkles } from 'ui/src/theme/sprinkles.css'
import { PriceIntentNotification } from '../../PriceIntentNotification'
import { usePriceIntent } from '../../usePriceIntent'
import { priceCalculatorAddedOffer } from '../priceCalculatorAtoms'
import { SectionSubtitle } from '../SectionHeading'
import { SectionTitle } from '../SectionHeading'
import { OfferEditButton } from './OfferEditButton'
import { scrollToBottomButton } from './OfferPresenter.css'

const EMPLOYMENT_TYPE_FORM_ID = 'employment-type-form'

export function OfferSummary() {
  const targetRef = useRef(null)
  const hasScrolledPast = useHasScrolledPast({ targetRef, offset: 800 })

  const { t } = useTranslation('purchase-form')
  const tracking = useTracking()

  const shopSession = useShopSessionValueOrThrow()
  const selectedOffer = useSelectedOfferValueOrThrow()
  const { priceIntent, resetPriceIntent } = usePriceIntent()
  const offerToEdit = useOfferToEdit()
  const setAddedOffer = useSetAtom(priceCalculatorAddedOffer)
  const { setStep } = usePriceCalculatorStep()

  const productOfferIds = useMemo(() => {
    if (!priceIntent) {
      return []
    }

    return priceIntent.offers.map(({ id }) => id)
  }, [priceIntent])

  const [employmentType, setEmploymentType, loadingEmploymentType] = useOptimisticEmploymentType({
    productOfferIds,
    initialValue: selectedOffer.priceIntentData.employmentType,
  })

  const [addToCart, loadingAddToCart] = useAddToCart({
    shopSessionId: shopSession.id,
    entryToReplace: offerToEdit?.isInCart ? offerToEdit.offer.id : undefined,
    onSuccess(productOfferId) {
      if (!priceIntent) {
        return
      }

      const addedProductOffer = priceIntent.offers.find((offer) => offer.id === productOfferId)

      if (addedProductOffer === undefined) {
        throw new Error(`Unknown offer added to cart: ${productOfferId}`)
      }

      resetPriceIntent()
      tracking.reportAddToCart(addedProductOffer, 'store')
      window.scrollTo({ top: 0, behavior: 'instant' })
      setAddedOffer(addedProductOffer)
      // Reset price calculator step to initial state
      setStep('loadingForm')
    },
  })

  const handleAddToCart = async () => {
    await addToCart(selectedOffer.id)
  }

  const handleScrollToBottom = () => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    window.scrollTo({ top: window.document.body.scrollHeight, behavior: prefersReducedMotion ? 'auto' : 'smooth' })
  }

  if (!priceIntent) {
    return null
  }

  // We don't want to show the cart discount when there is a young pet discount
  const youngPetDiscount = includesYoungPetDiscount(selectedOffer.cost)
  const shouldShowCartDiscount = shopSession.cart.campaignsEnabled && !youngPetDiscount
  const shouldShowYoungPetNotification = youngPetDiscount && shopSession.cart.redeemedCampaignV2

  const employmentTypeNotification = employmentType ? getEmploymentTypeNotification(employmentType, t) : undefined

  const notifications = [
    ...(priceIntent.notifications ?? []),
    ...(employmentTypeNotification ? [employmentTypeNotification] : []),
  ]

  return (
    <section>
      <SectionTitle>{t('OFFER_PRESENTER_SUMMARY_TITLE')}</SectionTitle>
      <SectionSubtitle className={sprinkles({ marginBottom: 'lg' })}>
        {t('OFFER_PRESENTER_SUMMARY_SUBTITLE')}
      </SectionSubtitle>
      <ProductCard.Root offer={selectedOffer}>
        <ProductCard.Header />

        <ProductCard.Details>
          <OfferEditButton />
        </ProductCard.Details>

        {isPaymentProtectionOffer(selectedOffer) ? (
          <div className={yStack({ gap: 'xs' })}>
            <CancellationForm productOfferIds={productOfferIds} offer={selectedOffer} />

            <form
              id={EMPLOYMENT_TYPE_FORM_ID}
              onSubmit={(e) => {
                e.preventDefault()
                handleAddToCart()
              }}
            >
              <EmploymentTypeSelect
                value={employmentType}
                onValueChange={setEmploymentType}
                required={true}
                loading={loadingEmploymentType}
              />
            </form>
          </div>
        ) : (
          <CancellationForm productOfferIds={productOfferIds} offer={selectedOffer} />
        )}

        {shouldShowCartDiscount && (
          <>
            <CartDiscount shopSession={shopSession} />
            <Divider />
          </>
        )}

        <div ref={targetRef}>
          <ProductCard.Breakdown />
        </div>

        <Divider />

        <ProductCard.TotalPrice />

        {notifications.length > 0 && (
          <div className={yStack({ alignItems: 'stretch', gap: 'xs', mt: 'xs' })}>
            {notifications.map(({ type, message }, index) => {
              return (
                <PriceIntentNotification key={index} type={type}>
                  {message}
                </PriceIntentNotification>
              )
            })}
          </div>
        )}

        {shouldShowYoungPetNotification && <YoungPetDiscountNotification selectedOffer={selectedOffer} />}

        {isPaymentProtectionOffer(selectedOffer) ? (
          <AddToCartButton
            form={EMPLOYMENT_TYPE_FORM_ID}
            type="submit"
            loading={loadingAddToCart}
            disabled={loadingEmploymentType || (employmentType && isUnsupportedEmploymentType(employmentType))}
          />
        ) : (
          <AddToCartButton type="button" onClick={handleAddToCart} loading={loadingAddToCart} />
        )}
      </ProductCard.Root>

      <Button
        onClick={handleScrollToBottom}
        className={clsx(scrollToBottomButton.base, hasScrolledPast && scrollToBottomButton.hidden)}
        size="small"
        variant="secondary"
        Icon={<ArrowDownIcon />}
        aria-label={t('SCROLL_TO_BOTTOM', { ns: 'common' })}
      />
    </section>
  )
}

function YoungPetDiscountNotification({ selectedOffer }: { selectedOffer: ProductOfferFragment }) {
  const { t } = useTranslation('purchase-form')
  const isCat = selectedOffer.product.name.includes('SE_PET_CAT')
  const youngPetMessage = isCat ? t('YOUNG_PET_CAT_DISCOUNT_NOTIFICATION') : t('YOUNG_PET_DOG_DISCOUNT_NOTIFICATION')
  return <InfoCard>{youngPetMessage}</InfoCard>
}

function AddToCartButton(props: Omit<ComponentProps<typeof Button>, 'variant' | 'size' | 'fullWidth'>) {
  const { t } = useTranslation('purchase-form')

  return (
    <Button variant="primary" size="large" fullWidth={true} {...props}>
      {t('ADD_TO_CART_BUTTON_LABEL')}
    </Button>
  )
}
